import { useUnit } from 'effector-react'
import { FormProvider, useForm } from 'react-hook-form'
import type { InfiniteData } from 'react-query'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form/form-field'
import type { CollectionsItem } from '@/dialogs/CollectionsDialog/CollectionsList/types'
import { $creatorId } from '@/models/creators.ts'
import { listsService } from '@/modules/chatting/service/lists'
import type { GetListsResponse } from '@/modules/chatting/types'
import { handleError } from '@/utils/error'

import { DialogProps } from '../types'
import {
  RENAME_COLLECTION_FORM_ID,
  RenameCollectionFormValues,
  renameCollectionFormResolver,
} from './validation'

interface Props extends DialogProps {
  collection: CollectionsItem
}

export const RenameCollectionDialog = ({
  isOpen,
  onClose,
  collection,
}: Props) => {
  const form = useForm<RenameCollectionFormValues>({
    defaultValues: { name: collection.name },
    resolver: renameCollectionFormResolver,
  })
  const creatorId = useUnit($creatorId)
  const queryClient = useQueryClient()
  const renameMutation = useMutation(
    (newName: RenameCollectionFormValues['name']) =>
      listsService.renameList({
        creatorId: creatorId!,
        listId: collection.id,
        newName,
      }),
    {
      onSuccess: (_, newName) => {
        queryClient.setQueriesData<InfiniteData<GetListsResponse> | undefined>(
          ['all-lists-extended', creatorId],
          (oldData) => {
            if (!oldData) return oldData

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                list: page.list.map((l) =>
                  l.id === collection.id ? { ...l, name: newName } : l,
                ),
              })),
            }
          },
        )
        onClose()
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while renaming list')
      },
    },
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      bigDialog
      panelClassName="lg:max-w-[533px]"
    >
      <form
        id={RENAME_COLLECTION_FORM_ID}
        className="space-y-6"
        onSubmit={form.handleSubmit(({ name }) =>
          renameMutation.mutateAsync(name),
        )}
      >
        <Dialog.Header>
          <Dialog.Title>Rename List</Dialog.Title>
        </Dialog.Header>
        <FormProvider {...form}>
          <FormField
            label="List Name"
            name="name"
          />
        </FormProvider>
        <Dialog.ClosableFooter
          onClose={onClose}
          disabled={form.formState.isSubmitting}
        >
          <Button
            form={RENAME_COLLECTION_FORM_ID}
            isLoading={renameMutation.isLoading}
            disabled={!form.formState.isDirty}
          >
            Save
          </Button>
        </Dialog.ClosableFooter>
      </form>
    </Dialog>
  )
}
